import React, { useState } from 'react';
import { FileText, Upload, Eye, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import Papa from 'papaparse';

const ImportEmailTemplates = () => {
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState([]);
  const [showAllRecords, setShowAllRecords] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [namePrefix, setNamePrefix] = useState('');

  const dbFields = [
    { value: 'name', label: 'Template Name' },
    { value: 'subject', label: 'Subject Line' },
    { value: 'body', label: 'Email Body' },
    { value: '_skip', label: '-- Skip Column --' }
  ];

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const hdrs = results.meta.fields;
          setHeaders(hdrs);
          setCsvData(results.data);

          const autoMapping = {};
          hdrs.forEach(header => {
            const lowerHeader = header.toLowerCase().trim();
            if (lowerHeader.includes('subject')) autoMapping[header] = 'subject';
            else if (lowerHeader.includes('body')) autoMapping[header] = 'body';
            else if (lowerHeader.includes('name') || lowerHeader.includes('company') || lowerHeader.includes('contact')) autoMapping[header] = 'name';
            else autoMapping[header] = '_skip';
          });
          setMapping(autoMapping);
          updatePreview(results.data.slice(0, 5), autoMapping);
        }
      });
    }
  };

  const updatePreview = (data, currentMapping) => {
    const mapped = data.map(row => {
      const mappedRow = {};
      Object.keys(currentMapping).forEach(csvCol => {
        const dbField = currentMapping[csvCol];
        if (dbField && dbField !== '_skip') {
          const value = row[csvCol];
          if (value && value.trim()) mappedRow[dbField] = value.trim();
        }
      });
      return mappedRow;
    });

    if (mapped.length > 0) {
      const fieldsWithValues = new Set();
      mapped.forEach(row => Object.keys(row).forEach(field => { if (row[field]) fieldsWithValues.add(field); }));
      setPreview(mapped.map(row => {
        const filtered = {};
        fieldsWithValues.forEach(field => { filtered[field] = row[field] || ''; });
        return filtered;
      }));
    } else {
      setPreview(mapped);
    }
  };

  const handleMappingChange = (csvColumn, dbField) => {
    const newMapping = { ...mapping, [csvColumn]: dbField };
    setMapping(newMapping);
    updatePreview(showAllRecords ? csvData : csvData.slice(0, 5), newMapping);
  };

  const viewAllRecords = () => {
    setShowAllRecords(true);
    updatePreview(csvData, mapping);
  };

  const importToDatabase = async () => {
    setIsImporting(true);
    setImportResult(null);

    try {
      const hasNameCol = Object.values(mapping).includes('name');
      const templates = csvData.map(row => {
        const tpl = {};
        Object.keys(mapping).forEach(csvCol => {
          const dbField = mapping[csvCol];
          if (dbField && dbField !== '_skip') {
            const value = row[csvCol];
            if (value && value.trim()) tpl[dbField] = value.trim();
          }
        });
        if (!hasNameCol && tpl.subject) {
          tpl.name = namePrefix ? `${namePrefix} - ${tpl.subject}`.slice(0, 100) : tpl.subject.slice(0, 60);
        }
        return tpl;
      }).filter(tpl => Object.keys(tpl).length > 0);

      const response = await axios.post('/api/email-templates/import', { templates, defaultType: 'Cold Leads' });
      setImportResult({
        success: true,
        message: `Successfully imported ${response.data.imported} templates`,
        imported: response.data.imported,
        skipped: response.data.skipped || 0,
        skippedTemplates: response.data.skippedTemplates || []
      });
    } catch (error) {
      console.error('Import error:', error);
      setImportResult({
        success: false,
        message: error.response?.data?.error || 'Failed to import templates. Please try again.'
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">CSV Email Template Importer</h1>
              <p className="text-gray-600">Upload your CSV and map fields to import email templates</p>
            </div>
            <FileText className="w-12 h-12 text-indigo-500" />
          </div>

          {/* File Upload */}
          <div className="mb-8">
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-all">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="w-10 h-10 text-gray-400 mb-3" />
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">CSV files only</p>
              </div>
              <input type="file" accept=".csv" onChange={handleFileUpload} className="hidden" />
            </label>
          </div>

          {/* Mapping Section */}
          {headers.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Field Mapping</h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {headers.map((header, index) => (
                    <div key={header} className="flex items-center gap-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          CSV: Col {index + 1} - {header}
                        </label>
                        <input
                          type="text"
                          value={`Col ${index + 1} - ${header}`}
                          disabled
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-sm text-gray-600"
                        />
                      </div>
                      <div className="text-gray-400">→</div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Database Field</label>
                        <select
                          value={mapping[header] || '_skip'}
                          onChange={(e) => handleMappingChange(header, e.target.value)}
                          className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${mapping[header] && mapping[header] !== '_skip' ? 'border-green-500 bg-green-50 text-green-800' : 'border-gray-300'}`}
                        >
                          {dbFields.map(field => (
                            <option key={field.value} value={field.value}>{field.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Name Rule — only shown when no column is mapped to name */}
          {headers.length > 0 && !Object.values(mapping).includes('name') && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Template Name Rule</h2>
              <div className="bg-gray-50 rounded-lg p-6 max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name Prefix <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={namePrefix}
                  onChange={(e) => setNamePrefix(e.target.value)}
                  placeholder="e.g. Bumble Outreach"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {namePrefix
                    ? `Names will be: "${namePrefix} - {Subject Line}"`
                    : 'Leave blank to use the subject line as the template name'}
                </p>
              </div>
            </div>
          )}

          {/* Preview Section */}
          {preview.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                {showAllRecords ? `All Records (${preview.length})` : `Preview (First 5 Records)`}
              </h2>
              <div className="bg-gray-50 rounded-lg p-6 overflow-x-auto">
                {Object.keys(preview[0]).length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        {Object.keys(preview[0]).map(field => (
                          <th key={field} className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider bg-gray-100 max-w-xs">
                            {field}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {preview.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          {Object.values(row).map((value, vIdx) => (
                            <td key={vIdx} className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={value || ''}>
                              {value || <span className="text-gray-400 italic">empty</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No data to preview. All mapped columns appear to be empty or skipped.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* View All Button */}
          {csvData.length > 0 && !showAllRecords && (
            <div className="flex gap-4 mb-8">
              <button
                onClick={viewAllRecords}
                className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
              >
                <Eye className="w-5 h-5" />
                View All Records ({csvData.length} total)
              </button>
            </div>
          )}

          {/* Import Button */}
          {csvData.length > 0 && showAllRecords && !importResult?.success && (
            <div className="flex gap-4 mb-8">
              <button
                onClick={importToDatabase}
                disabled={isImporting}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {isImporting ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Import to Database ({csvData.length} records)
                  </>
                )}
              </button>
            </div>
          )}

          {/* Import Result */}
          {importResult && (
            <div className={`mb-8 p-4 rounded-lg border ${importResult.success ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
              <div className="flex items-center gap-2">
                {importResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                )}
                <p className="font-medium">{importResult.message}</p>
              </div>
              {importResult.success && importResult.skipped > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-gray-700">{importResult.skipped} records were skipped</p>
                  {importResult.skippedTemplates && importResult.skippedTemplates.length > 0 && (
                    <details className="mt-2 text-sm">
                      <summary className="cursor-pointer text-gray-600 hover:text-gray-800">View skipped records</summary>
                      <ul className="mt-2 space-y-1 pl-4">
                        {importResult.skippedTemplates.map((tpl, i) => (
                          <li key={i} className="text-gray-600">
                            <span className="font-medium">{tpl.subject || tpl.name || 'Unnamed'}</span>: {tpl.reason}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportEmailTemplates;
